import {
  ChatChartEvent,
  ChatDeltaEvent,
  ChatDoneEvent,
  ChatErrorEvent,
  ChatMemoryEvent,
  ChatStartEvent,
  ChatTableEvent,
  ChatToolResultEvent,
  ChatToolUseEvent,
  ChatUsageEvent,
} from 'src/api/FiretrolApiClient';
import { resolveApiOrigin } from 'src/api/apiOrigin';
import { getAuthOptions } from 'src/api/helpers';
import { logger } from 'src/utils';

export type ChatStreamEvent =
  | (ChatStartEvent & { type: 'start' })
  | (ChatDeltaEvent & { type: 'delta' })
  | (ChatToolUseEvent & { type: 'tool_use' })
  | (ChatToolResultEvent & { type: 'tool_result' })
  | (ChatChartEvent & { type: 'chart' })
  | (ChatTableEvent & { type: 'table' })
  | (ChatMemoryEvent & { type: 'memory' })
  | (ChatUsageEvent & { type: 'usage' })
  | (ChatDoneEvent & { type: 'done' })
  | (ChatErrorEvent & { type: 'error' });

export interface StreamChatOptions {
  conversationId: string;
  message: string;
  page?: string;
  /** Supersedes this user message and every later one; the server rejects it unless it is the latest user message. */
  replaceFromMessageId?: string;
  thinkHarder?: boolean;
  signal?: AbortSignal;
  onEvent: (event: ChatStreamEvent) => void;
}

const baseUrl = resolveApiOrigin(import.meta.env.VITE_API);

/**
 * Streams an assistant reply for a conversation via raw `fetch`, since the
 * generated Axios client buffers responses and cannot consume SSE.
 */
export const streamChat = async ({
  conversationId,
  message,
  page,
  replaceFromMessageId,
  thinkHarder,
  signal,
  onEvent,
}: StreamChatOptions): Promise<void> => {
  const authOptions = await getAuthOptions();
  if (!authOptions) {
    throw new Error('Unable to acquire an auth token.');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Authorization: `Bearer ${authOptions.headers.Authorization}`,
  };
  if (authOptions.headers['X-Assume-User-Id']) {
    headers['X-Assume-User-Id'] = authOptions.headers['X-Assume-User-Id'];
  }

  const response = await fetch(
    `${baseUrl}/Chatbot/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message,
        page,
        replaceFromMessageId,
        thinkHarder,
      }),
      signal,
    },
  );

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const dispatchLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(':') || !trimmed.startsWith('data:')) {
      return;
    }
    const payload = trimmed.slice('data:'.length).trim();
    if (!payload) return;
    try {
      onEvent(JSON.parse(payload) as ChatStreamEvent);
    } catch (err) {
      logger.error('Failed to parse chat stream event', { err, payload });
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // Keep the last (possibly partial) line buffered until more data arrives.
    buffer = lines.pop() ?? '';
    lines.forEach(dispatchLine);
  }

  if (buffer.trim()) {
    dispatchLine(buffer);
  }
};
